// eslint-disable-next-line no-restricted-imports
import styled from '@emotion/styled';
import type {
  BrandSpecial,
  Cart as V3Cart,
  StoreSpecial as V3StoreSpecial,
  MaterializedMenuProduct,
} from '@iheartjane/data-access';
import { ALL_STORE_SPECIALS_OPTS } from '@iheartjane/data-access';
import {
  useStoreSpecials,
  useAddCartProduct as useV3AddCartProduct,
  useUpdateCartProduct as useV3UpdateCartProduct,
} from '@iheartjane/data-access/react-query';
import { FLAGS, useFlag } from '@iheartjane/feature-flags';
import { useNavigateBack } from '@iheartjane/menu';
import {
  ArrowLeftIcon,
  BagIcon,
  Box,
  Button,
  Flex,
  Link,
  List,
  Typography,
  useToast,
} from '@iheartjane/reefer';
import capitalize from 'lodash-es/capitalize';
import { darken } from 'polished';
import { useCallback, useMemo } from 'react';
import { Outlet, useNavigate } from 'react-router';

import { useCartTopperRow } from '@jane/dm/internal';
import {
  Alert,
  CartLimitPolicyAlert,
  CartRowActions,
  CartToppers,
  CartTotals,
  IconButton,
  JaneGoldCallout,
  ProductAddedToast,
  ProductRow,
} from '@jane/kiosk/components';
import type { CartRowProduct } from '@jane/kiosk/components/lib/ProductRow/cartRowProduct';
import type { AddToBagParams, RootLoaderType } from '@jane/kiosk/data-access';
import {
  useGetKiosk,
  useKioskUser,
  useUpdateCartProduct,
} from '@jane/kiosk/data-access';
import {
  useKioskCart,
  useKioskSpacing,
  useShouldKioskShowGold,
  useTypedRouteLoaderData,
} from '@jane/kiosk/hooks';
import { useBrandSpecials, useProductTracking } from '@jane/kiosk/providers';
import {
  getUsername,
  isAcceptingOrders,
  isSupportedStoreSpecial,
  nextAvailableSchedule,
  trackAddedProductToBag,
  trackCheckoutInitiated,
} from '@jane/kiosk/util';
import { trackCashBackPromptTap } from '@jane/kiosk/util/lib/tracking/track';
import {
  useAddCartProduct,
  useGetStoreSpecials,
} from '@jane/shared-ecomm/data-access';
import { cartSize } from '@jane/shared-ecomm/util';
import { useGetPosCart } from '@jane/shared/data-access';
import type { PendingCartProduct, StoreSpecial } from '@jane/shared/types';
import {
  availableWeightsForMenuProduct,
  calculateDiscount,
  calculateDiscountPercentage,
  generateCartLimitReport,
  getBundlesInProgress,
} from '@jane/shared/util';

import styles from './Cart.module.css';

const ContinueShoppingButton = styled(Button)(() => ({
  paddingLeft: 8,
  paddingRight: 8,
}));

const EmptyBag = styled(Flex)(({ theme }) => ({
  alignItems: 'center',
  backgroundColor: theme.colors.grays.ultralight,
  borderRadius: 24,
  flexDirection: 'column',
  grow: 1,
  height: '100%',
  justifyContent: 'center',
  paddingBottom: 12,
  paddingTop: 12,
}));

const OuterBox = styled(Flex)(({ theme }) => ({
  backgroundColor: theme.colors.grays.white,
  flexDirection: 'column',
  minHeight: '100vh',
  width: '100vw',
}));

const StyledButton = styled(Button)(({ theme }) => {
  const { getKioskSpacing } = useKioskSpacing();
  const paddingY = getKioskSpacing(4, 2);
  const color = darken(0.05, theme.colors.grays.ultralight);

  return {
    border: 'unset',
    backgroundColor: color,
    borderRadius: 32,
    height: 'auto',
    padding: `${paddingY}px 12px ${paddingY}px 12px`,
  };
});

export const Cart = () => {
  const showGold = useShouldKioskShowGold();
  const {
    kiosk: initialKiosk,
    kioskId,
    store,
    storeId,
  } = useTypedRouteLoaderData<RootLoaderType>('root');
  const {
    inlineAds: { inlineAdIds },
    cartToppers: { cartTopperIds, addCartTopperId },
  } = useProductTracking();

  const { data: kiosk } = useGetKiosk({
    initialData: initialKiosk,
    kioskId,
  });

  const useV3Cart = useFlag(FLAGS.c3UseV3Cart);
  const { data: cart, isFetching: isCartFetching } = useKioskCart();

  const { data: v1PosCart, isFetching: isV1PosCartFetching } = useGetPosCart(
    cart.uuid ?? '',
    { enabled: !useV3Cart && !!cart.uuid }
  );
  const posCart = useV3Cart ? (cart as V3Cart).pos_cart : v1PosCart;
  const isPosCartFetching = useV3Cart ? isCartFetching : isV1PosCartFetching;

  const isCartTotalsFetching = isCartFetching || isPosCartFetching;

  const { data: userData } = useKioskUser();

  const userSignedIn = !!userData;
  const userName = useMemo(() => getUsername(userData), [userData]);

  const useV3 = useFlag(FLAGS.c3UseV3StoreSpecials);
  const { data: v1Specials = [] } = useGetStoreSpecials(storeId, {
    enabled: !useV3 && !!storeId,
  });
  const v3SpecialsQuery = useStoreSpecials({
    ...ALL_STORE_SPECIALS_OPTS,
    enabled: !!useV3 && !!storeId,
    storeId,
  });
  const specials = useMemo<(StoreSpecial | V3StoreSpecial)[]>(() => {
    if (useV3) {
      return v3SpecialsQuery.data?.pages.flatMap((page) => page.specials) ?? [];
    }
    return v1Specials;
  }, [useV3, v3SpecialsQuery.data, v1Specials]);
  const { brandSpecials } = useBrandSpecials();
  const { mutate: updateProduct } = useUpdateCartProduct({});
  const { mutate: addCartProduct } = useAddCartProduct({});
  const { mutate: v3UpdateProduct } = useV3UpdateCartProduct({});
  const { mutate: v3AddCartProduct } = useV3AddCartProduct({});

  const navigate = useNavigate();
  const goBack = useNavigateBack(`stores/${storeId}/menu`);
  const toast = useToast();

  const cartProductIds = useMemo(
    // eslint-disable-next-line react-hooks/preserve-manual-memoization
    () => cart?.products?.map(({ product_id }) => product_id) ?? [],
    [cart?.products]
  );

  const { isLoading, instance } = useCartTopperRow<MaterializedMenuProduct>({
    cartProductIds,
    searchAttributes: ['*'],
    storeId,
  });

  const cartProductsCount = cartSize(cart);
  const cartLimitReport = generateCartLimitReport({
    cart,
    store,
  });
  const supportedStoreSpecials = specials.filter((special) =>
    isSupportedStoreSpecial(special, true)
  );

  const bundlesInProgress = getBundlesInProgress({
    brandSpecials,
    cart,
  });
  const isCartEmpty = useMemo(
    () => cartProductsCount === 0,
    [cartProductsCount]
  );

  const isStoreAcceptingOrders = isAcceptingOrders(store);
  const nextSchedule = nextAvailableSchedule(store);

  const { defaultSpacing, getKioskSpacing, isIpad } = useKioskSpacing();

  const onRemoveProduct = useCallback(
    (productId: CartRowProduct['product_id']) => {
      navigate(`/stores/${storeId}/cart/remove/${productId}`);
    },
    [navigate, storeId]
  );

  const onUpdateProduct = useCallback(
    (product: CartRowProduct, quantity: string) => {
      if (useV3Cart) {
        v3UpdateProduct({
          appMode: 'kiosk',
          cartUuid: cart.uuid,
          productId: product.product_id,
          count: parseInt(quantity),
          priceId: product.price_id,
        });
      } else {
        updateProduct({
          cart_uuid: cart.uuid,
          product: { ...product, count: parseInt(quantity) },
        });
      }

      // carts-v3 rule 3: read the precomputed discounted price instead of
      // recomputing via the io-ts isDiscountSupported gate. Gate on the same
      // supported-store-specials membership the legacy util applied.
      const originalPrice = product[`price_${product.price_id}`];
      const hasSupportedSpecial =
        product.special_id != null &&
        supportedStoreSpecials.some(({ id }) => id === product.special_id);
      const discountedPrice = hasSupportedSpecial
        ? product[`discounted_price_${product.price_id}`]
        : undefined;

      const isTopper = cartTopperIds.includes(product.product_id);
      const isInlineAd = inlineAdIds.includes(product.product_id);

      trackAddedProductToBag({
        cart,
        dmType: isTopper ? ['bag'] : isInlineAd ? ['in-line'] : null,
        productDetails: {
          // TODO(carts-v3): rule 1/3 — trackAddedProductToBag is typed on the
          // legacy product union; backward-cast the v3-ready CartRowProduct to
          // the legacy cart type until tracking reads a narrow interface.
          product: product as PendingCartProduct,
          productPercentageOff: discountedPrice
            ? calculateDiscountPercentage(
                Number(originalPrice) * Number(quantity),
                Number(discountedPrice) * Number(quantity)
              )
            : null,
          productPriceId: product.price_id,
          productQuantity: Number(quantity) - product.count,
          productSavingsAmount: discountedPrice
            ? calculateDiscount(
                Number(originalPrice) * Number(quantity),
                Number(discountedPrice) * Number(quantity)
              )
            : null,
          productSpecialId: product.special_id,
        },
        quickAdd: false,
        source: ['bag'],
        store,
      });
    },
    [
      cart,
      store,
      updateProduct,
      v3UpdateProduct,
      useV3Cart,
      supportedStoreSpecials,
      cartTopperIds,
      inlineAdIds,
    ]
  );

  const onAddCartTopper = useCallback(
    ({ priceId, product, productInstance }: AddToBagParams) => {
      const weights = availableWeightsForMenuProduct(product);

      if (weights.length > 1) {
        productInstance.click();
        navigate(
          `/stores/${storeId}/cart/select-addon-weight/${product.product_id}?priceId=${priceId}&topper=true`
        );
      } else {
        productInstance.click({ isAddToCart: true });
        addCartTopperId(product.product_id);
        if (useV3Cart) {
          v3AddCartProduct({
            appMode: 'kiosk',
            cartUuid: cart.uuid,
            storeId,
            product: {
              count: 1,
              price_id: priceId,
              product_id: product.product_id,
              recommended: false,
            },
          });
        } else {
          addCartProduct({
            app_mode: 'kiosk',
            app_mode_name: 'kiosk',
            cart_uuid: cart.uuid,
            product: {
              count: 1,
              price_id: priceId,
              product_id: product.product_id,
              recommended: false,
            },
            store_id: storeId,
            user_agent: window.navigator.userAgent,
          });
        }

        toast.add({
          label: <ProductAddedToast productCount={1} storeId={storeId} />,
        });
      }
    },
    [
      addCartProduct,
      v3AddCartProduct,
      useV3Cart,
      addCartTopperId,
      cart.uuid,
      navigate,
      storeId,
      toast,
    ]
  );

  const onProductClick = useCallback(
    (product: CartRowProduct) => {
      navigate(
        `/stores/${storeId}/cart/products/${product.product_id}?priceId=${product.price_id}&pdpDepth=1`
      );
    },
    [navigate, storeId]
  );

  const renderCartProduct = useCallback(
    (product: CartRowProduct) => {
      const janeGoldTagLabel = showGold
        ? product.brand_discount_title || product.brand_discount_label
        : null;

      return (
        <List.Item
          disableHover
          key={`${product.product_id}-${product.price_id}`}
          px={0}
          py={0}
        >
          <ProductRow
            actions={
              <CartRowActions
                onChangeQuantity={onUpdateProduct}
                onRemove={onRemoveProduct}
                product={product}
              />
            }
            janeGoldTagLabel={janeGoldTagLabel}
            product={product}
            priceId={product.price_id}
            onProductImageClick={onProductClick}
            showRating={false}
          />
        </List.Item>
      );
    },
    [onProductClick, onRemoveProduct, onUpdateProduct]
  );

  const onContinueShopping = useCallback(() => {
    navigate(`/stores/${storeId}/menu`);
  }, [navigate, storeId]);

  const onCheckout = useCallback(() => {
    trackCheckoutInitiated({
      cart,
      kiosk,
      specials,
      store,
    });
    navigate(
      `/stores/${storeId}/cart/checkout${
        kiosk?.checkout_on_separate_device ? `-on-device` : ''
      }`
    );
  }, [cart, navigate, specials, store, storeId, kiosk]);

  const onBrandSpecialClick = (brandSpecial: BrandSpecial) => {
    trackCashBackPromptTap({ brandSpecial, source: ['Cart'] });

    navigate(`/stores/${store.id}/menu/jane-gold/${brandSpecial.id}`);
  };

  return (
    <OuterBox pb={getKioskSpacing(48)}>
      <Flex
        flexDirection="column"
        pt={userSignedIn ? getKioskSpacing(24, 8) : getKioskSpacing(72, 48)}
        mx={getKioskSpacing(64, 48)}
      >
        {userSignedIn && (
          <StyledButton
            variant="tertiary"
            size="small"
            label={
              userName?.includes('privaterelay.appleid.com')
                ? "Didn't sign in? Logout"
                : `Not ${userName}? Logout`
            }
            ml="auto"
            mb={getKioskSpacing(16, 12)}
            onClick={() => navigate(`/stores/${store.id}/cart/logout`)}
          />
        )}
        <Flex
          alignItems="center"
          flexDirection="row"
          pb={getKioskSpacing(40, 32)}
        >
          <IconButton
            ariaLabel="navigate back"
            onClick={() => goBack()}
            icon={
              <ArrowLeftIcon color="grays-black" size={isIpad ? 'lg' : 'xl'} />
            }
            style={{ boxShadow: '2px 4px 14px 0 rgba(0, 0, 0, 0.08)' }}
          />
          <Typography
            ml={defaultSpacing}
            variant={isIpad ? 'h2-regular' : 'h1-regular'}
            branded
          >
            Your bag
          </Typography>
        </Flex>

        <Box
          className={styles['boxShadow']}
          borderRadius="lg"
          p={getKioskSpacing(40, 32)}
          mb={getKioskSpacing(24, 16)}
        >
          <CartTotals
            cart={cart}
            posCart={posCart}
            isLoading={isCartTotalsFetching}
          />
        </Box>

        {showGold && !!cart.brand_discounts_total_with_fee && (
          <JaneGoldCallout
            variant="cart"
            brandDiscounts={cart.brand_discounts}
            brandDiscountsServiceFeeTotal={
              cart.brand_discounts_service_fee_total
            }
            brandDiscountsTotal={cart.brand_discounts_total ?? 0}
          />
        )}
        <Flex
          flexDirection="row"
          gap={getKioskSpacing(24, 16)}
          mb={getKioskSpacing(48, 24)}
        >
          <ContinueShoppingButton
            ariaLabel="continue shopping"
            full
            label={
              <Typography
                color="text-main"
                variant={isIpad ? 'body-semibold' : 'h4-semibold'}
                as="div"
                branded
              >
                Continue shopping
              </Typography>
            }
            onClick={onContinueShopping}
            variant="tertiary"
            size={isIpad ? 'small' : 'large'}
          />
          <Button
            disabled={
              !cart?.uuid || cartLimitReport.over || !isStoreAcceptingOrders
            }
            full
            label={
              <Typography
                color="text-info"
                variant={isIpad ? 'body-semibold' : 'h4-semibold'}
                as="div"
                branded
              >
                Checkout
              </Typography>
            }
            onClick={onCheckout}
            variant="primary"
            size={isIpad ? 'small' : 'large'}
          />
        </Flex>
      </Flex>

      <Flex
        flexDirection="column"
        grow={1}
        mx={getKioskSpacing(64, 48)}
        height="100%"
        mb={isIpad ? 8 : 'auto'}
      >
        {!isStoreAcceptingOrders && (
          <Alert
            label={`Bummer! We're closed for the day. ${
              nextSchedule && nextSchedule.day && nextSchedule.period.from
                ? `You can place an order again on ${capitalize(
                    nextSchedule.day
                  )}.`
                : ''
            }`}
          />
        )}
        {!isCartEmpty ? (
          <>
            {isStoreAcceptingOrders && (
              <CartLimitPolicyAlert report={cartLimitReport} />
            )}

            <Typography
              variant={isIpad ? 'body-semibold' : 'h3-semibold'}
              as="h2"
            >
              Your items
            </Typography>
            {bundlesInProgress.length > 0 && (
              <JaneGoldCallout
                variant="bundleProgress"
                bundlesInProgress={bundlesInProgress}
                cart={cart}
                onClick={(brandSpecial) => onBrandSpecialClick(brandSpecial)}
              />
            )}
            <Box
              borderRadius="lg"
              border="transparent-black-five"
              mb={getKioskSpacing(48, 24)}
              mt={getKioskSpacing(24, 16)}
              pt={getKioskSpacing(48, 32)}
              pl={getKioskSpacing(48, 32)}
              pr={getKioskSpacing(24, 12)}
            >
              <List label="cart-products-list">
                {(cart.products ?? []).map(renderCartProduct)}
              </List>
            </Box>
          </>
        ) : (
          <EmptyBag
            alignItems="center"
            grow={1}
            height="100%"
            justifyContent="center"
            flexDirection="column"
          >
            <BagIcon mb={32} size="xxxl" />
            <Typography variant="body-semibold">
              Your bag is empty. Continue shopping to add products.
            </Typography>
            <Link
              typography="body-regular"
              mt={16}
              to={`/stores/${storeId}/menu`}
              ariaLabel="shop all products"
            >
              Shop now
            </Link>
          </EmptyBag>
        )}
      </Flex>

      {!isCartEmpty && (
        <Flex flexDirection="column" ml={getKioskSpacing(64, 48)}>
          <CartToppers
            onAddCartTopper={onAddCartTopper}
            isLoading={isLoading}
            row={instance}
          />
        </Flex>
      )}
      <Outlet />
    </OuterBox>
  );
};
